import {Alert} from '@mui/material';

export const getAudioDurationFromUrl = (url: string): Promise<number> => {
  return new Promise((resolve, reject) => {
    const audio = document.createElement('audio');
    audio.src = url;

    // Clean up the object URL after use
    const onLoaded = () => {
      cleanup();
      resolve(audio.duration);
    };

    const onError = () => {
      cleanup();
      reject(new Error('Failed to load audio'));
    };

    const cleanup = () => {
      audio.removeEventListener('loadedmetadata', onLoaded);
      audio.removeEventListener('error', onError);
    };

    audio.addEventListener('loadedmetadata', onLoaded);
    audio.addEventListener('error', onError);

    // Preload the audio
    audio.preload = 'metadata';
  });
};

export const getAudioDurationFromFile = async (file: File): Promise<number> => {
  const url = URL.createObjectURL(file);
  try {
    const duration = await getAudioDurationFromUrl(url);
    return duration;
  } finally {
    // Always revoke the object URL to prevent memory leaks
    URL.revokeObjectURL(url);
  }
};

const AudioDurationValidation = ({
  maxDurationInMin,
  durationInSec,
}: {
  maxDurationInMin: number;
  durationInSec: number;
}) => {
  if (durationInSec / 60 >= maxDurationInMin) {
    return (
      <Alert severity='error' sx={{my: 1}}>
        Max Song Duration {maxDurationInMin} minutes
      </Alert>
    );
  }
  return null;
};

export default AudioDurationValidation;

export const AudioSizeValidation = ({
  fileSize,
  maxSizeInMb,
}: {
  fileSize: number;
  maxSizeInMb: number;
}) => {
  if (fileSize / (1000 * 1000) > maxSizeInMb) {
    return (
      <Alert severity='error' sx={{my: 1}}>
        File size is more than {maxSizeInMb}MB
      </Alert>
    );
  }
  return null;
};

export const isFilesExceedTheDuration = async (
  files: File[],
  maxDuration: number,
) => {
  if (maxDuration) {
    const durationPromises = files.map(file => getAudioDurationFromFile(file));
    const durations = await Promise.all(durationPromises);

    if (durations.some(duration => maxDuration < duration)) {
      return true;
    }
  }
  return false;
};
